import React from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { CapacityPlannerScreen } from '../../screen/CapacityPlanner/Screen';
import { projectColor, formatDate } from './types';

interface Props { planner: CapacityPlannerScreen; }

@observer
export class TimeTracking extends React.Component<Props> {
    render() {
        const p = this.props.planner;
        if (!p.trackingOpen) return null;
        return (
            <div className="tracking-view" data-test-cp-tracking-view>
                <header className="tracking-header">
                    <button className="back-btn" onClick={p.closeTimeTracking}>← <span>Back to Planning</span></button>
                    <h2>Time Tracking</h2>
                    <div className="tracking-tabs">
                        <button className={'tab' + (p.trackingTab === 'entry' ? ' active' : '')} onClick={() => p.switchTrackingTab('entry')}>Log Time</button>
                        <button className={'tab' + (p.trackingTab === 'analytics' ? ' active' : '')} onClick={() => p.switchTrackingTab('analytics')}>Analytics</button>
                    </div>
                </header>
                <div className="tracking-body">
                    {p.trackingTab === 'entry' && this.renderTimeEntryPanel()}
                    {p.trackingTab === 'analytics' && this.renderAnalyticsPanel()}
                </div>
            </div>
        );
    }

    renderTimeEntryPanel() {
        const p = this.props.planner;
        const devProjects = p.entryDeveloperId ? p.getDevProjects(p.entryDeveloperId) : [];
        return (
            <aside className="time-entry-panel" style={{ display: p.trackingTab === 'entry' ? 'flex' : 'none' }}>
                <div className="time-entry-header">
                    <h3>Log Hours</h3>
                    <div className="date-selector">
                        <button className="btn btn-sm" onClick={p.prevDay}>&lt;</button>
                        <input type="date" value={moment(p.trackingDate).format('YYYY-MM-DD')}
                            onChange={(e) => p.trackingDate = new Date(e.target.value)} />
                        <button className="btn btn-sm" onClick={p.nextDay}>&gt;</button>
                        <button className="btn btn-sm" onClick={p.goToToday}>Today</button>
                    </div>
                </div>
                <div className="time-entry-form">
                    <div className="entry-type-toggle">
                        <button className={'type-btn' + (p.entryType === 'planned' ? ' active' : '')} onClick={() => p.setEntryType('planned')}>Planned Work</button>
                        <button className={'type-btn support' + (p.entryType === 'support' ? ' active' : '')} onClick={() => p.setEntryType('support')}>Support/Unplanned</button>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Developer</label>
                            <select value={p.entryDeveloperId || ''} onChange={(e) => { p.entryDeveloperId = parseInt(e.target.value); p.entryProjectId = null; p.entryFeatureId = null; }}>
                                {p.users.map(u => <option key={u.id} value={u.id}>{(u as any).fullName}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Project</label>
                            <select value={p.entryProjectId || ''} onChange={(e) => { p.entryProjectId = parseInt(e.target.value); p.entryFeatureId = null; }}>
                                <option value="">Select project...</option>
                                {devProjects.map(proj => <option key={proj.id} value={proj.id}>{proj.name}</option>)}
                            </select>
                        </div>
                    </div>
                    {p.entryType === 'planned' && p.entryProjectId && (
                        <div className="form-row">
                            <div className="form-group">
                                <label>Feature</label>
                                <select value={p.entryFeatureId || ''} onChange={(e) => p.entryFeatureId = parseInt(e.target.value) || null}>
                                    <option value="">General work</option>
                                    {p.entryProjectId && p.getProjectTickets(p.projects.find(pr => pr.id === p.entryProjectId)).map(t => (
                                        <option key={t.id} value={t.id}>{(t as any).title || (t as any).fullName}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                    <div className="form-row">
                        <div className="form-group hours-input">
                            <label>Hours</label>
                            <input type="number" min="0.5" max="12" step="0.5" value={p.entryHours}
                                onChange={(e) => p.entryHours = parseFloat(e.target.value) || 1} />
                        </div>
                        <div className="form-group">
                            <label>Description (optional)</label>
                            <input type="text" placeholder="What did you work on?" value={p.entryDescription}
                                onChange={(e) => p.entryDescription = e.target.value} />
                        </div>
                    </div>
                    <button className="btn btn-primary" style={{ width: '100%' }} onClick={p.addTimeEntry}>Log Time</button>
                </div>
                <div className="time-entries-list">
                    {p.dayEntries.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--cp-text-secondary)' }}>
                            No time logged for {formatDate(p.trackingDate)}
                        </div>
                    ) : p.dayEntries.map(entry => {
                        const proj = p.projects.find(pr => pr.id === entry.projectId);
                        const dev = p.users.find(u => u.id === entry.developerId);
                        const t = entry.featureId ? p.tickets.find(tk => tk.id === entry.featureId) : null;
                        return (
                            <div key={entry.id} className={'time-entry-item' + (entry.type === 'support' ? ' support' : '')}>
                                <div className="entry-header">
                                    <div className="entry-project" style={{ color: proj ? projectColor(proj) : '#666' }}>{proj?.name || 'Unknown'}</div>
                                    <div className="entry-hours">{entry.hours}h</div>
                                </div>
                                {t && <div className="entry-feature">{(t as any).title || (t as any).fullName}</div>}
                                {entry.type === 'support' && <div className="entry-feature" style={{ color: 'var(--cp-warning)' }}>Support/Unplanned</div>}
                                {entry.description && <div className="entry-description">"{entry.description}"</div>}
                                <div style={{ fontSize: '0.7rem', color: 'var(--cp-text-secondary)', marginTop: '0.25rem' }}>{(dev as any)?.fullName}</div>
                                <div className="entry-actions">
                                    <button className="btn-icon delete" onClick={() => p.deleteTimeEntry(entry.id)} title="Delete">🗑</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="day-total">
                    <span>Day Total</span>
                    <span className="total-hours">{p.dayTotalHours}h</span>
                </div>
            </aside>
        );
    }

    renderAnalyticsPanel() {
        const p = this.props.planner;
        const entries = p.entriesForPeriod;
        const totalHours = entries.reduce((s, e) => s + e.hours, 0);
        const plannedHours = entries.filter(e => e.type === 'planned').reduce((s, e) => s + e.hours, 0);
        const supportHours = entries.filter(e => e.type === 'support').reduce((s, e) => s + e.hours, 0);
        let periodDays = 60;
        if (p.analyticsPeriod === 'week') {
            periodDays = 5;
        } else if (p.analyticsPeriod === 'month') {
            periodDays = 20;
        }
        let expectedHours = 0;
        p.users.forEach(u => { expectedHours += (p.getDevWeeklyHours(u) / 5) * periodDays; });
        const projectStats: Record<number, any> = {};
        p.projects.forEach(proj => {
            projectStats[proj.id] = { project: proj, planned: 0, actual: 0, support: 0 };
            const allocs = p.getProjectAllocations(proj);
            allocs.forEach(a => {
                const u = p.users.find(ux => ux.id === (a as any).user?.id) as any;
                if (u) {
                    const wh = p.getDevWeeklyHours(u) * (p.getDaysPerWeek(a) / 5);
                    projectStats[proj.id].planned += (wh / 5) * periodDays;
                }
            });
        });
        entries.forEach(e => {
            if (projectStats[e.projectId]) {
                projectStats[e.projectId].actual += e.hours;
                if (e.type === 'support') projectStats[e.projectId].support += e.hours;
            }
        });
        const devStats: Record<number, any> = {};
        p.users.forEach(u => { devStats[u.id] = { developer: u, total: 0, byProject: {} as Record<number, number> }; });
        entries.forEach(e => {
            if (devStats[e.developerId]) {
                devStats[e.developerId].total += e.hours;
                devStats[e.developerId].byProject[e.projectId] = (devStats[e.developerId].byProject[e.projectId] || 0) + e.hours;
            }
        });
        let periodLabel = 'all time';
        if (p.analyticsPeriod === 'week') {
            periodLabel = 'this week';
        } else if (p.analyticsPeriod === 'month') {
            periodLabel = 'this month';
        }
        return (
            <div className="analytics-panel">
                <div className="analytics-header">
                    <h3>Time Analysis</h3>
                    <div className="period-selector">
                        <button className={'period-btn' + (p.analyticsPeriod === 'week' ? ' active' : '')} onClick={() => p.setPeriod('week')}>This Week</button>
                        <button className={'period-btn' + (p.analyticsPeriod === 'month' ? ' active' : '')} onClick={() => p.setPeriod('month')}>This Month</button>
                        <button className={'period-btn' + (p.analyticsPeriod === 'all' ? ' active' : '')} onClick={() => p.setPeriod('all')}>All Time</button>
                    </div>
                </div>
                <div className="analytics-content">
                    <div className="summary-cards">
                        <div className="summary-card">
                            <div className="card-label">Total Logged</div>
                            <div className="card-value">{totalHours}h</div>
                            <div className="card-subtext">{periodLabel}</div>
                        </div>
                        <div className={'summary-card' + (plannedHours < expectedHours * 0.8 ? ' warning' : ' success')}>
                            <div className="card-label">Planned Work</div>
                            <div className="card-value">{plannedHours}h</div>
                            <div className="card-subtext">{totalHours > 0 ? Math.round(plannedHours / totalHours * 100) : 0}% of total</div>
                        </div>
                        <div className={'summary-card' + (supportHours > totalHours * 0.2 ? ' danger' : '')}>
                            <div className="card-label">Support/Unplanned</div>
                            <div className="card-value">{supportHours}h</div>
                            <div className="card-subtext">{totalHours > 0 ? Math.round(supportHours / totalHours * 100) : 0}% of total</div>
                        </div>
                        <div className="summary-card">
                            <div className="card-label">Team Capacity</div>
                            <div className="card-value">{expectedHours > 0 ? Math.round(totalHours / expectedHours * 100) : 0}%</div>
                            <div className="card-subtext">of expected {Math.round(expectedHours)}h</div>
                        </div>
                    </div>
                    {supportHours > 0 && (
                        <div className="support-impact">
                            <h4>⚠️ Support Impact Analysis</h4>
                            <p style={{ fontSize: '0.8rem', marginBottom: '0.75rem', color: 'var(--cp-text-primary)' }}>
                                {supportHours}h spent on unplanned work. This may be impacting project timelines.
                            </p>
                            <div className="support-breakdown">
                                {Object.values(projectStats).filter((ps: any) => ps.support > 0).sort((a: any, b: any) => b.support - a.support).map((ps: any) => (
                                    <div key={ps.project.id} className="support-item">
                                        <div className="support-project" style={{ color: projectColor(ps.project) }}>{ps.project.name}</div>
                                        <div className="support-hours">{ps.support}h</div>
                                        <div className="support-percent">{Math.round(ps.support / supportHours * 100)}% of support time</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="comparison-section">
                        <h4>📊 Planned vs Actual by Project</h4>
                        <table className="comparison-table">
                            <thead>
                                <tr><th>Project</th><th>Planned</th><th>Actual</th><th>Variance</th><th>Progress</th></tr>
                            </thead>
                            <tbody>
                                {Object.values(projectStats).map((ps: any) => {
                                    const variance = ps.actual - ps.planned;
                                    const progress = ps.planned > 0 ? (ps.actual / ps.planned * 100) : 0;
                                    let progressClass = '';
                                    if (progress > 110) {
                                        progressClass = 'over';
                                    } else if (progress > 90) {
                                        progressClass = 'warning';
                                    }
                                    return (
                                        <tr key={ps.project.id}>
                                            <td>
                                                <div className="project-cell">
                                                    <div className="project-color" style={{ background: projectColor(ps.project) }} />
                                                    {ps.project.name}
                                                </div>
                                            </td>
                                            <td className="hours-cell">{Math.round(ps.planned)}h</td>
                                            <td className="hours-cell">{ps.actual}h</td>
                                            <td className={'variance-cell' + (variance > 0 ? ' over' : ' under')}>{variance > 0 ? '+' : ''}{Math.round(variance)}h</td>
                                            <td>
                                                <div className="progress-bar-container">
                                                    <div className="progress-bar">
                                                        <div className="planned" style={{ width: '100%' }} />
                                                        <div className={'actual' + (progressClass ? ' ' + progressClass : '')} style={{ width: `${Math.min(progress, 150)}%` }} />
                                                    </div>
                                                    <div className="progress-label">{Math.round(progress)}% of planned</div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="comparison-section">
                        <h4>👥 Time by Developer</h4>
                        <div className="developer-breakdown">
                            {Object.values(devStats).map((ds: any) => {
                                const maxHours = Math.max(...Object.values(ds.byProject) as number[], 1);
                                return (
                                    <div key={ds.developer.id} className="dev-time-card">
                                        <div className="dev-header">
                                            <div className="dev-avatar" style={{ background: projectColor(ds.developer) }}>{(ds.developer.fullName || '?').charAt(0)}</div>
                                            <div>
                                                <div className="dev-name">{ds.developer.fullName}</div>
                                                <div className="dev-total">{ds.total}h total</div>
                                            </div>
                                        </div>
                                        <div className="project-bars">
                                            {Object.entries(ds.byProject).map(([pid, hours]) => {
                                                const proj = p.projects.find(pr => pr.id === parseInt(pid));
                                                return (
                                                    <div key={pid} className="project-bar-item">
                                                        <div className="bar-label" title={proj?.name}>{proj?.name || 'Unknown'}</div>
                                                        <div className="bar-track">
                                                            <div className="bar-fill" style={{ width: `${(hours as number) / maxHours * 100}%`, background: proj ? projectColor(proj) : '#666' }} />
                                                        </div>
                                                        <div className="bar-hours">{hours as number}h</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
